// client/src/FeaturePlots.jsx

/**
 * The feature-relationship panel, shown on demand during the prediction stages:
 * the plots themselves, and nothing else.
 *
 * The panel is the shape of each relationship -- whether a variable moves the
 * outcome steeply or barely at all -- and reading a particular borrower's value off
 * it is left to the participant. The summary table it used to carry now appears
 * only in the variable introduction, where there is time to study it.
 *
 * Sizing note: the images are 2500x1500 (aspect 1.667). Height is fixed and width
 * follows the aspect ratio, so the row's height is predictable no matter how many
 * plots are shown (3 without AI, 4 with).
 */

import React from "react";
import dtiGraph from "./intro-exit/Debt-to-Income_graph.png";
import creditLimitGraph from "./intro-exit/Total_Credit_Limit_graph.png";
import creditScoreGraph from "./intro-exit/Credit_Score_graph.png";
import revolveGraph from "./intro-exit/Revolving_Utilization_graph.png";
import aiGraph from "./intro-exit/AI_Risk_Score_graph.png";
import { variable_theme } from "./theme.js";

// Back to the original height now that the table below the plots is gone -- the
// panel is a row of images and nothing else, so the 10% it had been cut by to make
// room for the table is no longer needed.
const PLOT_HEIGHT_PX = 145;

export default function FeaturePlots({ isExpert, hasAI }) {
  const tPublic = variable_theme.public;
  const tPrivate = variable_theme.private;
  const tAI = variable_theme.ai;

  const plots = [
    { src: dtiGraph, label: "Debt-to-Income", color: tPublic.textColor },
    { src: creditLimitGraph, label: "Total Credit Limit", color: tPublic.textColor },
    {
      src: isExpert ? creditScoreGraph : revolveGraph,
      label: isExpert ? "Credit Score" : "Revolving Utilization",
      color: tPrivate.textColor,
    },
    ...(hasAI
      ? [{ src: aiGraph, label: "AI Risk Recommendation", color: tAI.textColor }]
      : []),
  ];

  // No caption above each plot: every image already carries its own axis title
  // ("Debt-to-Income", "AI Risk Score", ...), so a label would repeat it and cost
  // ~20px of the height budget for nothing.
  return (
    <div className="pt-1">
      <div className="flex flex-wrap items-start justify-center gap-3">
        {plots.map((plot) => (
          <img
            key={plot.label}
            src={plot.src}
            alt={`How ${plot.label} relates to default probability`}
            className="w-auto object-contain"
            style={{ height: `${PLOT_HEIGHT_PX}px` }}
          />
        ))}
      </div>

    </div>
  );
}
